import { unknownToError, unknownToString } from "@dhau/lang-extras";
import { captureException } from "@sentry/react";
import { backOff } from "exponential-backoff";
import { print, type DocumentNode } from "graphql";

function isError(error: unknown): error is Error {
	return (
		typeof error === "object" &&
		!!error &&
		"message" in error &&
		typeof error.message === "string"
	);
}

class ApiError extends Error {
	readonly graphqlErrors: readonly Error[];

	public constructor(graphqlErrors: readonly Error[]) {
		super(graphqlErrors.map((e) => e.message).join("\n"));

		this.graphqlErrors = graphqlErrors;
	}
}

type RequestOptions = {
	readonly signal?: AbortSignal;
};

function createApiClient(url: string) {
	async function performRequest<
		TResult,
		TVariables extends Record<string, unknown>,
	>(
		query: DocumentNode,
		variables: TVariables | undefined,
		options?: RequestOptions,
	) {
		let response: Response;
		try {
			response = await fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					query: print(query),
					variables,
				}),
				signal: options?.signal,
			});
		} catch (e) {
			if (options?.signal?.aborted) {
				throw e;
			}
			throw new ApiError([unknownToError(e)]);
		}

		const { data, errors } = await response.json();

		if (Array.isArray(errors)) {
			const graphqlErrors = errors.filter(isError);
			if (graphqlErrors.length < errors.length) {
				throw new Error(
					`Unknown server error: ${errors.map(unknownToString).join(", ")}`,
				);
			}
			throw new ApiError(graphqlErrors);
		}

		return data as TResult;
	}

	async function query<TQuery, TVariables extends Record<string, unknown>>(
		query: DocumentNode,
		variables: TVariables,
		options?: RequestOptions,
	): Promise<TQuery>;

	async function query<TQuery>(
		query: DocumentNode,
		variables?: undefined,
		options?: RequestOptions,
	): Promise<TQuery>;

	async function query<TQuery, TVariables extends Record<string, unknown>>(
		query: DocumentNode,
		variables?: TVariables,
		options?: RequestOptions,
	): Promise<TQuery> {
		try {
			return await backOff(
				() => performRequest<TQuery, TVariables>(query, variables, options),
				{
					jitter: "none",
					maxDelay: 10_000,
					numOfAttempts: 8,
					startingDelay: 100,
					timeMultiple: 2,
					retry() {
						return !options?.signal?.aborted;
					},
				},
			);
		} catch (e) {
			if (!options?.signal?.aborted) {
				captureException(e);
			}
			throw e;
		}
	}

	async function mutate<TMutation, TVariables extends Record<string, unknown>>(
		mutation: DocumentNode,
		variables: TVariables,
		options?: RequestOptions,
	): Promise<TMutation>;

	async function mutate<TMutation>(
		mutation: DocumentNode,
		variables?: undefined,
		options?: RequestOptions,
	): Promise<TMutation>;

	async function mutate<TMutation, TVariables extends Record<string, unknown>>(
		mutation: DocumentNode,
		variables?: TVariables,
		options?: RequestOptions,
	): Promise<TMutation> {
		try {
			return await performRequest<TMutation, TVariables>(
				mutation,
				variables,
				options,
			);
		} catch (e) {
			if (!options?.signal?.aborted) {
				captureException(e);
			}
			throw e;
		}
	}

	return {
		query,
		mutate,
	};
}

type ApiClient = ReturnType<typeof createApiClient>;

export type { ApiClient };
export { createApiClient, ApiError };
